import { useRef } from 'react'
import { Controller, Control, FieldError } from 'react-hook-form'
import Select, { SelectInstance } from 'react-select'
import { ContactFormValues } from '@utils'
import { ContactMessage, CityOption } from '@types'
import {
    ariaLiveFr,
    createSelectKeyDownHandler,
    screenReaderStatusFr,
} from '@components/blog/selectA11yFr'
import DropdownIndicator from './dropdownIndicator'
import FormField from './formField'

interface CitySelectProps {
    message: ContactMessage
    cityOptions: CityOption[]
    control: Control<ContactFormValues>
    error?: FieldError
    isRequired?: boolean
}

const CitySelect = ({
    message,
    cityOptions,
    control,
    error,
    isRequired,
}: CitySelectProps) => {
    const fieldId = `contact-${message.title}`
    const labelText = isRequired ? 'Ville *' : 'Ville'
    const placeholderText = 'Sélectionner une ville'
    const selectInstanceRef = useRef<SelectInstance<CityOption, false> | null>(
        null
    )

    const sizingText =
        cityOptions.reduce((longest, opt) => {
            const label = opt.attributes.label ?? ''
            return label.length > longest.length ? label : longest
        }, placeholderText).length > placeholderText.length
            ? cityOptions.reduce((longest, opt) => {
                  const label = opt.attributes.label ?? ''
                  return label.length > longest.length ? label : longest
              }, placeholderText)
            : placeholderText

    const selectFocusControl = ({ isFocused }: { isFocused: boolean }) =>
        `flex border-2 w-full min-h-[2.75rem] items-center text-left pl-2 font-semibold text-base py-2 overflow-visible ${
            isFocused ? 'border-[#0a8335] rounded-md' : 'border-secondary-300'
        }`
    const selectFocusOption = ({ isFocused }: { isFocused: boolean }) =>
        `px-3 py-2 rounded-xl shadow-none cursor-pointer border-2 ${
            isFocused ? 'border-[#058943]' : 'border-transparent'
        }`

    return (
        <FormField
            fieldId={fieldId}
            infoText={labelText}
            error={error?.message}
            required={isRequired}
        >
            <div className="flex w-fit ml-auto my-2 overflow-visible">
                <Controller
                    name="city"
                    control={control}
                    render={({ field: { onChange, value, ref } }) => (
                        <div style={{ display: 'inline-block', width: '100%' }}>
                            <div
                                style={{
                                    height: '0',
                                    marginLeft: '2.6rem',
                                    visibility: 'hidden',
                                }}
                            >
                                {sizingText}
                            </div>
                            <Select<CityOption, false>
                                inputId={fieldId}
                                ref={(instance) => {
                                    selectInstanceRef.current = instance
                                    if (typeof ref === 'function') {
                                        ref(instance)
                                    } else if (ref) {
                                        ;(
                                            ref as React.MutableRefObject<unknown>
                                        ).current = instance
                                    }
                                }}
                                value={
                                    cityOptions.find(
                                        (opt) => opt.attributes.label === value
                                    ) ?? null
                                }
                                onChange={(option) =>
                                    onChange(option?.attributes?.label ?? '')
                                }
                                onKeyDown={createSelectKeyDownHandler(
                                    selectInstanceRef
                                )}
                                tabSelectsValue={false}
                                ariaLiveMessages={ariaLiveFr}
                                screenReaderStatus={screenReaderStatusFr}
                                aria-required={isRequired || undefined}
                                aria-invalid={error ? true : undefined}
                                aria-describedby={
                                    error ? `${fieldId}-error` : undefined
                                }
                                isSearchable={false}
                                hideSelectedOptions={true}
                                unstyled
                                components={{ DropdownIndicator }}
                                menuPortalTarget={
                                    typeof document !== 'undefined'
                                        ? document.body
                                        : null
                                }
                                menuPlacement="auto"
                                classNames={{
                                    singleValue: () => 'single-value',
                                    control: selectFocusControl,
                                    menu: () =>
                                        'shadow-lg drop-shadow-lg z-[100] overflow-hidden mt-1',
                                    menuList: () => 'p-1',
                                    option: selectFocusOption,
                                }}
                                styles={{
                                    container: (provided) => ({
                                        ...provided,
                                        width: '100%',
                                    }),
                                    control: (provided) => ({
                                        ...provided,
                                        backgroundColor: '#F7F7F8',
                                        gap: '0.5rem',
                                        padding: '0.5rem',
                                        width: '100%',
                                    }),
                                    dropdownIndicator: (base, state) => ({
                                        ...base,
                                        transition: 'all .4s ease',
                                        transform: state.selectProps.menuIsOpen
                                            ? 'rotate(180deg)'
                                            : 'initial',
                                    }),
                                    option: (provided, state) => ({
                                        ...provided,
                                        backgroundColor: 'white',
                                        color: 'inherit',
                                        textAlign: 'end',
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        paddingRight: '0.625rem',
                                        border: state.isFocused
                                            ? '2px solid #058943'
                                            : '2px solid transparent',
                                        outline: 'none',
                                        boxShadow: 'none',
                                        borderRadius: '0.5rem',
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        background: 'white',
                                        zIndex: 9999,
                                        width: '100%',
                                        border: '1px solid #6C737E',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                    }),
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}
                                formatOptionLabel={(data) => (
                                    <div className="city-choiceOption rounded px-1">
                                        {data.attributes.label}
                                    </div>
                                )}
                                options={cityOptions}
                                getOptionLabel={(option) =>
                                    `${option.attributes.label}`
                                }
                                getOptionValue={(option) => `${option.id}`}
                                placeholder={placeholderText}
                            />
                        </div>
                    )}
                />
            </div>
        </FormField>
    )
}

export default CitySelect
